import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '@/utils/api';
import type { Task, TaskFormData, TaskFilters, Subtask } from '@/types';

interface TimelineGroup {
  key: string;
  label: string;
  tasks: Task[];
}

interface CalendarDay {
  date: string;
  tasks: Task[];
}

interface TasksState {
  tasks: Task[];
  currentTask: Task | null;
  subtasks: Record<string, Subtask[]>;
  filters: TaskFilters;
  timelineGroups: TimelineGroup[];
  calendarDays: CalendarDay[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
}

const initialState: TasksState = {
  tasks: [],
  currentTask: null,
  subtasks: {},
  filters: {},
  timelineGroups: [],
  calendarDays: [],
  isLoading: false,
  error: null,
  totalCount: 0,
};

export const fetchTasks = createAsyncThunk(
  'tasks/fetchTasks',
  async (filters: TaskFilters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (filters.status?.length) params.set('status', filters.status.join(','));
      if (filters.type?.length) params.set('type', filters.type.join(','));
      if (filters.priority?.length) params.set('priority', filters.priority.join(','));
      if (filters.topicId) params.set('topicId', filters.topicId);
      if (filters.tags?.length) params.set('tags', filters.tags.join(','));
      if (filters.search) params.set('search', filters.search);
      if (filters.taskStatus) params.set('taskStatus', filters.taskStatus);

      const response = await api.get(`/tasks?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch tasks'
      );
    }
  }
);

export const fetchTask = createAsyncThunk(
  'tasks/fetchTask',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch task'
      );
    }
  }
);

export const createTask = createAsyncThunk(
  'tasks/createTask',
  async (data: TaskFormData, { rejectWithValue }) => {
    try {
      const response = await api.post('/tasks', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to create task'
      );
    }
  }
);

export const updateTask = createAsyncThunk(
  'tasks/updateTask',
  async ({ id, data }: { id: string; data: Partial<Task> }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/tasks/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to update task'
      );
    }
  }
);

export const deleteTask = createAsyncThunk(
  'tasks/deleteTask',
  async (taskId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/tasks/${taskId}`);
      return taskId;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to delete task'
      );
    }
  }
);

export const fetchSubtasks = createAsyncThunk(
  'tasks/fetchSubtasks',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/tasks/${taskId}/subtasks`);
      return { taskId, subtasks: response.data };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch subtasks'
      );
    }
  }
);

export const createSubtask = createAsyncThunk(
  'tasks/createSubtask',
  async ({ taskId, title }: { taskId: string; title: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/tasks/${taskId}/subtasks`, { title });
      return { taskId, subtask: response.data };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to create subtask'
      );
    }
  }
);

export const updateSubtask = createAsyncThunk(
  'tasks/updateSubtask',
  async (
    { taskId, subtaskId, data }: { taskId: string; subtaskId: string; data: Partial<Subtask> },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.put(`/tasks/${taskId}/subtasks/${subtaskId}`, data);
      return { taskId, subtask: response.data };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to update subtask'
      );
    }
  }
);

export const deleteSubtask = createAsyncThunk(
  'tasks/deleteSubtask',
  async ({ taskId, subtaskId }: { taskId: string; subtaskId: string }, { rejectWithValue }) => {
    try {
      await api.delete(`/tasks/${taskId}/subtasks/${subtaskId}`);
      return { taskId, subtaskId };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to delete subtask'
      );
    }
  }
);

// Trash (soft delete) a task
export const trashTask = createAsyncThunk(
  'tasks/trashTask',
  async (taskId: string, { rejectWithValue }) => {
    try {
      await api.post(`/tasks/${taskId}/trash`);
      return taskId;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to trash task'
      );
    }
  }
);

// Restore a task from trash or backlog
export const restoreTask = createAsyncThunk(
  'tasks/restoreTask',
  async (taskId: string, { rejectWithValue }) => {
    try {
      await api.post(`/tasks/${taskId}/restore`);
      return taskId;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to restore task'
      );
    }
  }
);

// Move task to backlog
export const backlogTask = createAsyncThunk(
  'tasks/backlogTask',
  async (taskId: string, { rejectWithValue }) => {
    try {
      await api.post(`/tasks/${taskId}/backlog`);
      return taskId;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to move task to backlog'
      );
    }
  }
);

// Fetch timeline view data
export const fetchTimelineView = createAsyncThunk(
  'tasks/fetchTimelineView',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/tasks/view/timeline');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch timeline'
      );
    }
  }
);

// Fetch calendar view data
export const fetchCalendarView = createAsyncThunk(
  'tasks/fetchCalendarView',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/tasks/view/calendar');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch calendar'
      );
    }
  }
);

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<TaskFilters>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setCurrentTask: (state, action: PayloadAction<Task | null>) => {
      state.currentTask = action.payload;
    },
    updateTaskTimeSpent: (state, action: PayloadAction<{ taskId: string; timeSpent: number }>) => {
      const task = state.tasks.find((t) => t.id === action.payload.taskId);
      if (task) {
        task.timeSpent = action.payload.timeSpent;
      }
      if (state.currentTask?.id === action.payload.taskId) {
        state.currentTask.timeSpent = action.payload.timeSpent;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch tasks
      .addCase(fetchTasks.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tasks = action.payload.items || action.payload;
        state.totalCount = action.payload.total || action.payload.length;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch single task
      .addCase(fetchTask.fulfilled, (state, action) => {
        state.currentTask = action.payload;
      })
      // Create task
      .addCase(createTask.fulfilled, (state, action) => {
        state.tasks.unshift(action.payload);
        state.totalCount += 1;
      })
      // Update task
      .addCase(updateTask.fulfilled, (state, action) => {
        const index = state.tasks.findIndex((t) => t.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
        if (state.currentTask?.id === action.payload.id) {
          state.currentTask = action.payload;
        }
      })
      // Delete task
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.tasks = state.tasks.filter((t) => t.id !== action.payload);
        state.totalCount -= 1;
        if (state.currentTask?.id === action.payload) {
          state.currentTask = null;
        }
      })
      // Fetch subtasks
      .addCase(fetchSubtasks.fulfilled, (state, action) => {
        state.subtasks[action.payload.taskId] = action.payload.subtasks;
      })
      // Create subtask
      .addCase(createSubtask.fulfilled, (state, action) => {
        const { taskId, subtask } = action.payload;
        if (!state.subtasks[taskId]) {
          state.subtasks[taskId] = [];
        }
        state.subtasks[taskId].push(subtask);
      })
      // Update subtask
      .addCase(updateSubtask.fulfilled, (state, action) => {
        const { taskId, subtask } = action.payload;
        const subtasks = state.subtasks[taskId];
        if (subtasks) {
          const index = subtasks.findIndex((s) => s.id === subtask.id);
          if (index !== -1) {
            subtasks[index] = subtask;
          }
        }
      })
      // Delete subtask
      .addCase(deleteSubtask.fulfilled, (state, action) => {
        const { taskId, subtaskId } = action.payload;
        const subtasks = state.subtasks[taskId];
        if (subtasks) {
          state.subtasks[taskId] = subtasks.filter((s) => s.id !== subtaskId);
        }
      })
      // Trash task (soft delete)
      .addCase(trashTask.fulfilled, (state, action) => {
        state.tasks = state.tasks.filter((t) => t.id !== action.payload);
        state.totalCount -= 1;
        if (state.currentTask?.id === action.payload) {
          state.currentTask = null;
        }
      })
      // Restore task
      .addCase(restoreTask.fulfilled, (state, action) => {
        state.tasks = state.tasks.filter((t) => t.id !== action.payload);
        state.totalCount -= 1;
      })
      // Backlog task
      .addCase(backlogTask.fulfilled, (state, action) => {
        state.tasks = state.tasks.filter((t) => t.id !== action.payload);
        state.totalCount -= 1;
        if (state.currentTask?.id === action.payload) {
          state.currentTask = null;
        }
      })
      // Fetch timeline view
      .addCase(fetchTimelineView.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTimelineView.fulfilled, (state, action) => {
        state.isLoading = false;
        // Transform backend response into groups array
        const groups: TimelineGroup[] = [];
        const { today, yesterday, previous7Days, olderByMonth } = action.payload;

        if (today && today.length > 0) {
          groups.push({ key: 'today', label: 'Today', tasks: today });
        }
        if (yesterday && yesterday.length > 0) {
          groups.push({ key: 'yesterday', label: 'Yesterday', tasks: yesterday });
        }
        if (previous7Days && previous7Days.length > 0) {
          groups.push({ key: 'previous7Days', label: 'Previous 7 Days', tasks: previous7Days });
        }
        if (olderByMonth) {
          Object.entries(olderByMonth).forEach(([month, tasks]) => {
            if ((tasks as Task[]).length > 0) {
              groups.push({ key: month, label: month, tasks: tasks as Task[] });
            }
          });
        }

        state.timelineGroups = groups;
      })
      .addCase(fetchTimelineView.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch calendar view
      .addCase(fetchCalendarView.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCalendarView.fulfilled, (state, action) => {
        state.isLoading = false;
        // Transform backend byDate object into array format
        const { byDate } = action.payload;
        if (byDate) {
          state.calendarDays = Object.entries(byDate).map(([date, tasks]) => ({
            date,
            tasks: tasks as Task[],
          }));
        } else {
          state.calendarDays = [];
        }
      })
      .addCase(fetchCalendarView.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setFilters, clearFilters, setCurrentTask, updateTaskTimeSpent } = tasksSlice.actions;
export default tasksSlice.reducer;
